import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  ReferenceField,
  useRecordContext,
  EditButton,
  DeleteButton,
  ListButton,
  TopToolbar,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Paper,
  Chip,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AccountTree as DiagramIcon,
  Factory as PlantIcon,
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  List as ListIcon,
  Circle as NodeIcon,
  Link as EdgeIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const ShowTitle = () => {
  const record = useRecordContext();
  return record ? `Diagrama: ${record}` : 'Diagrama';
};

const ShowActions = () => (
  <TopToolbar>
    <EditButton icon={<EditIcon />} label='Editar' />
    <DeleteButton icon={<DeleteIcon />} label='Eliminar' />
    <ListButton icon={<ListIcon />} label='Volver a la Lista' />
  </TopToolbar>
);

const RolePermissionsField = () => {
  const record = useRecordContext();

  console.log('🔍 DiagramsShow RolePermissionsField - record completo:', record);
  console.log(
    '🔍 DiagramsShow RolePermissionsField - record.rolePermissions tipo:',
    typeof record?.rolePermissions
  );
  console.log(
    '🔍 DiagramsShow RolePermissionsField - record.rolePermissions valor:',
    record?.rolePermissions
  );
  console.log(
    '🔍 DiagramsShow RolePermissionsField - es array?:',
    Array.isArray(record?.rolePermissions)
  );

  if (!record || !record.rolePermissions || record.rolePermissions.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sin roles asignados
      </Typography>
    );
  }

  return (
    <Stack direction='column' spacing={1} flexWrap='wrap' useFlexGap>
      {record.rolePermissions.map((rp: any, index: number) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={rp.role}
            size='small'
            variant='outlined'
            color='primary'
          />
          <Chip
            label={rp.permissionLevel}
            size='small'
            variant='filled'
            color='secondary'
          />
        </Box>
      ))}
    </Stack>
  );
};

const NodesField = () => {
  const record = useRecordContext();

  if (!record?.nodes || record.nodes.length === 0) {
    return (
      <Alert severity='info' icon={<InfoIcon />}>
        Este diagrama no tiene nodos configurados
      </Alert>
    );
  }

  const nodesByType = record.nodes.reduce((acc: any, node: any) => {
    const type = node.nodeType || 'UNKNOWN';
    if (!acc[type]) acc[type] = [];
    acc[type].push(node);
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Nodos del Diagrama ({record.nodes.length})
      </Typography>

      {Object.entries(nodesByType).map(([type, nodes]: [string, any]) => (
        <Accordion key={type} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display='flex' alignItems='center' gap={1}>
              <NodeIcon color='primary' fontSize='small' />
              <Typography variant='subtitle1'>
                {type} ({(nodes as any[]).length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {(nodes as any[]).map((node: any, index: number) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant='subtitle2' color='primary'>
                      Nodo #{node.id}
                    </Typography>
                    {node.text && (
                      <Typography variant='body2'>
                        Texto: {node.text}
                      </Typography>
                    )}
                    {node.backgroundColor && (
                      <Box display='flex' alignItems='center' gap={1} mt={1}>
                        <Typography variant='caption'>Color:</Typography>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: node.backgroundColor,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant='caption'>
                          {node.backgroundColor}
                        </Typography>
                      </Box>
                    )}
                    {node.positionX !== undefined &&
                      node.positionY !== undefined && (
                        <Typography variant='caption' display='block' mt={1}>
                          Posición: ({node.positionX}, {node.positionY})
                        </Typography>
                      )}
                    {node.width && node.height && (
                      <Typography variant='caption' display='block'>
                        Tamaño: {node.width} × {node.height}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const EdgesField = () => {
  const record = useRecordContext();

  if (!record?.edges || record.edges.length === 0) {
    return (
      <Alert severity='info' icon={<InfoIcon />}>
        Este diagrama no tiene conexiones configuradas
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Conexiones del Diagrama ({record.edges.length})
      </Typography>

      <Grid container spacing={2}>
        {record.edges.map((edge: any, index: number) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box display='flex' alignItems='center' gap={1} mb={1}>
                <EdgeIcon color='secondary' fontSize='small' />
                <Typography variant='subtitle2' color='secondary'>
                  Conexión #{edge.id}
                </Typography>
              </Box>

              <Typography variant='body2'>
                Desde Nodo: {edge.sourceNodeId} → Nodo: {edge.targetNodeId}
              </Typography>

              {edge.edgeType && (
                <Typography variant='caption' display='block'>
                  Tipo: {edge.edgeType}
                </Typography>
              )}

              {edge.label && (
                <Typography variant='caption' display='block'>
                  Etiqueta: {edge.label}
                </Typography>
              )}

              {edge.color && (
                <Box display='flex' alignItems='center' gap={1} mt={1}>
                  <Typography variant='caption'>Color:</Typography>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: edge.color,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant='caption'>{edge.color}</Typography>
                </Box>
              )}

              {edge.animated && (
                <Chip
                  label='Animado'
                  size='small'
                  color='info'
                  variant='outlined'
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const DiagramsShow = () => {
  return (
    <Show title={<ShowTitle />} actions={<ShowActions />}>
      <Box sx={{ padding: 3, margin: '0 auto' }}>
        <SimpleShowLayout>
          {/* Información Principal */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Box display='flex' alignItems='center' mb={3}>
                <DiagramIcon
                  sx={{ mr: 2, fontSize: 32, color: 'primary.main' }}
                />
                <Box>
                  <Typography variant='h5' component='h1' fontWeight='bold'>
                    Información del Diagrama
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Detalles y configuración del diagrama
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      ID del Diagrama
                    </Typography>
                    <TextField source='id' />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Nombre
                    </Typography>
                    <TextField source='name' />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Descripción
                    </Typography>
                    <TextField source='description' />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Planta Asignada
                    </Typography>
                    <ReferenceField
                      source='plantId'
                      reference='plants'
                      link='show'
                    >
                      <Box display='flex' alignItems='center' gap={1}>
                        <PlantIcon color='primary' fontSize='small' />
                        <TextField source='plantName' />
                      </Box>
                    </ReferenceField>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Fecha de Creación
                    </Typography>
                    <DateField source='createdAt' showTime />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Última Actualización
                    </Typography>
                    <DateField source='updatedAt' showTime />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Roles de Acceso */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <GroupIcon
                  sx={{ mr: 2, fontSize: 24, color: 'primary.main' }}
                />
                <Typography variant='h6' fontWeight='bold'>
                  Roles de Acceso
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <RolePermissionsField />
            </CardContent>
          </Card>

          {/* Nodos del Diagrama */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <NodesField />
            </CardContent>
          </Card>

          {/* Conexiones del Diagrama */}
          <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <EdgesField />
            </CardContent>
          </Card>
        </SimpleShowLayout>
      </Box>
    </Show>
  );
};

export default DiagramsShow;
